import type {
  AuthCodeFlowSuccessResponse,
  AuthCodeFlowErrorResponse,
} from 'vue3-google-signin'
import type { UserInfo } from '../entities/UserInfo'
import fetchLoginGoogle from '~~/modules/api/rurality/v3/users/login-google'
import loggedUserRedirect from '~~/modules/auth/composables/useAuthRedirect'

export const useGoogleSignIn = (isModal?: boolean) => {
  const googleUserInfo = ref<UserInfo | undefined>(undefined)
  const googleApiResponseError: Ref<ApiResponseError | null> = ref(null)
  const googleLoginIsLoading = ref<boolean>(false)
  const showGoogleForm = ref<boolean>(false)
  const googleLoginSuccess = ref<boolean>(false)

  const getUserInfo = async (
    response: AuthCodeFlowSuccessResponse,
  ): Promise<void> => {
    const accessToken = response.access_token

    const googleResponse = await fetch(
      `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${accessToken}`,
    )

    if (!googleResponse.ok) {
      googleApiResponseError.value = {
        status: 'error',
        statusCode: 400,
        message: 'Failed to get Google UserData',
        errors: [],
      }
    }

    const googleInfo = await googleResponse.json()

    googleUserInfo.value = {
      emailAddress: googleInfo.email,
      firstName: googleInfo.given_name,
      lastName: googleInfo.family_name,
      id: googleInfo.id,
    }
  }

  const onSuccessGoogleSignInRegister = async (
    response: AuthCodeFlowSuccessResponse,
  ) => {
    await getUserInfo(response)
    if (googleUserInfo.value) {
      googleLoginIsLoading.value = true
      const login = await fetchLoginGoogle({
        emailAddress: googleUserInfo.value.emailAddress,
        googleId: googleUserInfo.value.id,
      })
      if (login.success && login.success.data) {
        googleLoginIsLoading.value = false
        if (isModal) {
          googleLoginSuccess.value = true
        } else {
          navigateTo(
            loggedUserRedirect({
              userType: login.success.data.userType,
              userId: login.success.data.userId,
            }),
            {
              external: true,
            },
          )
        }
      } else if (login.error) {
        googleLoginIsLoading.value = false
        showGoogleForm.value = true
      }
    }
  }

  const onSuccessGoogleSignInLogin = async (
    response: AuthCodeFlowSuccessResponse,
  ) => {
    await getUserInfo(response)

    if (googleUserInfo.value) {
      googleLoginIsLoading.value = true
      const login = await fetchLoginGoogle({
        emailAddress: googleUserInfo.value.emailAddress,
        googleId: googleUserInfo.value.id,
      })
      if (login.success && login.success.data) {
        googleLoginIsLoading.value = false
        if (isModal) {
          googleLoginSuccess.value = true
        } else {
          navigateTo(
            loggedUserRedirect({
              userType: login.success.data.userType,
              userId: login.success.data.userId,
            }),
            {
              external: true,
            },
          )
        }
      } else if (login.error) {
        googleLoginIsLoading.value = false
        googleApiResponseError.value = login.error
      }
    }
  }

  const onErrorGoogleSignIn = (errorResponse: AuthCodeFlowErrorResponse) => {
    if (errorResponse.error && errorResponse.error_description) {
      googleApiResponseError.value = {
        status: 'error',
        statusCode: 400,
        message: errorResponse.error_description,
        errors: [],
      }
    }
  }

  return {
    googleUserInfo,
    googleLoginIsLoading,
    googleApiResponseError,
    googleLoginSuccess,
    showGoogleForm,
    onSuccessGoogleSignInRegister,
    onSuccessGoogleSignInLogin,
    onErrorGoogleSignIn,
  }
}
